import './NetworkTree.scss'

import { DEBOUNCE_DELAY, LIVE_POOLING_INTERVAL } from '@utils/globals'
import {
    EuiButton,
    EuiButtonGroup,
    EuiButtonIcon,
    EuiEmptyPrompt,
    EuiFlexGroup,
    EuiFlexItem,
    EuiIcon,
    EuiProgress,
    EuiResizeObserver,
    EuiSpacer,
} from '@elastic/eui'
import { NetworkCollection, TreeMode, TreeState } from '@services/network'
import { setActiveNode, setTreeMode } from '@store/network'
import { useAppDispatch, useAppSelector } from '@hooks/store'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
    useFetchNetworkAgentsQuery,
    useFetchNetworkGroupsQuery,
} from '@services/api'
import { useLocation, useNavigate } from 'react-router-dom'

import { Agent } from '@services/agents'
import { Category } from '@services/categories'
import { Group } from '@services/groups'
import NetworkNode from '../NetworkTreeNode/NetworkNode'
import NetworkTreeFilters from './NetworkTreeFilters'
import { Permissions } from '@services/auth'
import { Tree } from 'react-arborist'
import { useDebounce } from '@hooks/utils'
import { useHasPermission } from '@hooks/auth'
import useNetworkTree from '@hooks/network/useNetworkTree'
import useQueryParamsNetworkData from '@hooks/utils/useQueryParamsNetworkData'
import { useTranslation } from 'react-i18next'

const defaultTreeState = {
    errors: false,
    warnings: false,
    expandAll: true,
    sort: true,
}

const NetworkTree = () => {
    const { t } = useTranslation(['common', 'network'])
    const navigate = useNavigate()
    const location = useLocation()

    // Permission
    const canViewGlobal = useHasPermission(Permissions.network.canViewGlobal)
    const dispatch = useAppDispatch()
    const { treeMode: storedTreeMode, refreshTree } = useAppSelector(
        (state) => state.network
    )
    const treeMode = !canViewGlobal ? 'groups' : storedTreeMode

    const [data, setData] = useState<
        NetworkCollection<Agent> | NetworkCollection<Group> | undefined
    >(undefined)

    // Search state
    const [treeState, setTreeState] = useState<TreeState>(defaultTreeState)
    const [queryState, setQueryState] = useState<TreeState>(defaultTreeState)
    const debounce = useDebounce(treeState, DEBOUNCE_DELAY)

    // Tree state
    const {
        treeRef,
        treeItems,
        onSearch,
        expandAllNodes,
        collapseAllNodes,
        onRenderRow,
    } = useNetworkTree({
        data: data,
        mode: treeMode,
    })

    const [treeDimenesions, setTreeDimensions] = useState<
        { height: number; width: number } | undefined
    >()

    // Find the sensor/device from the URL query params and the group data
    const { agent, group, device, sensor } = useQueryParamsNetworkData(location)
    const isQueryParamsApplied = useRef<boolean>(false)

    // Data fetching queries
    const {
        data: groups,
        isLoading: isGroupsLoading,
        refetch: groupsRefetch,
    } = useFetchNetworkGroupsQuery(queryState, {
        skip: treeMode !== 'groups',
        pollingInterval: LIVE_POOLING_INTERVAL,
    })
    const {
        data: agents,
        isLoading: isAgentsLoading,
        refetch: agentsRefetch,
    } = useFetchNetworkAgentsQuery(queryState, {
        skip: treeMode !== 'agents',
        pollingInterval: LIVE_POOLING_INTERVAL,
    })

    const forceRefresh = () => {
        if (treeMode === 'groups') {
            groupsRefetch()
        } else {
            agentsRefetch()
        }
    }

    const handleOnAddClick = () => {
        switch (treeMode) {
            case 'agents':
                navigate('/agents/new')
                break
            case 'groups':
                navigate('/network/group/new')
                break
        }
    }

    const handleOnCategoryChange = (categories: Category[]) => {
        setTreeState({
            ...queryState,
            categories: categories.map((c) => c.id),
        })
    }

    const getTreeModes = () => {
        const modes = [
            {
                id: 'groups',
                label: t('network:groups'),
            },
        ]

        if (canViewGlobal) {
            modes.push({
                id: 'agents',
                label: t('network:agents'),
            })
        }

        return modes
    }

    const tree = useMemo(
        () => (
            <Tree
                className="network-tree"
                ref={treeRef}
                searchTerm={treeState.search}
                searchMatch={onSearch}
                height={treeDimenesions?.height}
                width={treeDimenesions?.width}
                data={treeItems}
                rowHeight={26}
                idAccessor="id"
                childrenAccessor={(x) => x.children}
                disableDrag
                disableDrop
                disableEdit
                disableMultiSelection
                onActivate={(node) => node.toggle()}
                renderRow={onRenderRow}
            >
                {NetworkNode}
            </Tree>
        ),
        [treeState.search, treeDimenesions, treeItems, onRenderRow]
    )

    // Setting tree items
    useEffect(() => {
        const networkData = treeMode === 'groups' ? groups : agents
        setData(networkData)
    }, [groups, agents, treeMode])

    useEffect(() => {
        if (treeState.expandAll) {
            expandAllNodes()
        } else {
            collapseAllNodes()
        }
    }, [treeState.expandAll])

    useEffect(() => {
        forceRefresh()
    }, [refreshTree])

    useEffect(() => {
        if (debounce) {
            setQueryState({ ...debounce, sessionId: Date.now() })
        }
    }, [debounce])

    // If a sensor or device matching the URL query params has been found, set it as the active node
    useEffect(() => {
        if (sensor) {
            dispatch(
                setActiveNode({
                    data: { ...sensor },
                    type: 'sensor',
                })
            )
            if (!treeState.search && !isQueryParamsApplied.current) {
                setTreeState({ ...treeState, search: sensor.name })
                isQueryParamsApplied.current = true
            }
        } else if (device) {
            dispatch(
                setActiveNode({
                    data: { ...device },
                    type: 'device',
                })
            )
            if (!treeState.search && !isQueryParamsApplied.current) {
                setTreeState({ ...treeState, search: device.name })
                isQueryParamsApplied.current = true
            }
        } else if (group) {
            if (!treeState.search && !isQueryParamsApplied.current) {
                setTreeState({ ...treeState, search: group.name })
                isQueryParamsApplied.current = true
            }
        } else if (agent) {
            dispatch(setTreeMode('agents'))

            if (!treeState.search && !isQueryParamsApplied.current) {
                setTreeState({ ...treeState, search: agent.name })
                isQueryParamsApplied.current = true
            }
        }
    }, [agent, group, device, sensor, treeItems])

    return (
        <>
            <div>
                <EuiFlexGroup alignItems="center" justifyContent="spaceBetween">
                    <EuiFlexItem grow={false}>
                        <EuiButtonGroup
                            legend="tree mode"
                            color="text"
                            buttonSize="compressed"
                            options={getTreeModes()}
                            idSelected={treeMode}
                            onChange={(id) =>
                                dispatch(setTreeMode(id as TreeMode))
                            }
                        />
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiFlexGroup gutterSize="xs" alignItems="center">
                            <EuiFlexItem>
                                <EuiButtonIcon
                                    title={t(
                                        treeState.expandAll
                                            ? 'network:collapse_all'
                                            : 'network:expand_all'
                                    )}
                                    aria-label={t(
                                        treeState.expandAll
                                            ? 'network:collapse_all'
                                            : 'network:expand_all'
                                    )}
                                    display={
                                        treeState.expandAll ? 'fill' : 'empty'
                                    }
                                    iconType="submodule"
                                    size="xs"
                                    onClick={() =>
                                        setTreeState({
                                            ...treeState,
                                            expandAll: !treeState.expandAll,
                                        })
                                    }
                                />
                            </EuiFlexItem>
                            <EuiFlexItem>
                                <EuiButtonIcon
                                    title={t('network:tree_sort')}
                                    aria-label={t('network:tree_sort')}
                                    iconType="menuDown"
                                    size="xs"
                                    display={treeState.sort ? 'fill' : 'empty'}
                                    onClick={() =>
                                        setTreeState({
                                            ...treeState,
                                            sort: !treeState.sort,
                                        })
                                    }
                                />
                            </EuiFlexItem>
                            <EuiFlexItem>
                                <EuiButtonIcon
                                    title={t(`network:no_${treeMode}_add`)}
                                    aria-label={t(`network:no_${treeMode}_add`)}
                                    iconType="plus"
                                    size="xs"
                                    onClick={handleOnAddClick}
                                />
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    </EuiFlexItem>
                </EuiFlexGroup>
                <EuiSpacer size="m" />
                <NetworkTreeFilters
                    treeState={treeState}
                    onCategoryChange={handleOnCategoryChange}
                    onSearch={(value) => {
                        setTreeState({
                            ...treeState,
                            search: value,
                            expandAll: true,
                        })
                    }}
                />
                <EuiSpacer size="m" />
                <div style={{ position: 'relative' }}>
                    {isGroupsLoading || isAgentsLoading ? (
                        <>
                            <EuiProgress
                                size="xs"
                                color="primary"
                                position="absolute"
                            />
                            <EuiSpacer size="s" />
                        </>
                    ) : null}
                    <EuiResizeObserver onResize={setTreeDimensions}>
                        {(resizeRef) => {
                            return (
                                <div
                                    className="eui-yScrollWithShadows"
                                    aria-label={'tree-view'}
                                    ref={resizeRef}
                                    style={{
                                        minWidth: 350,
                                        height: 'calc(100vh - 363px)',
                                    }}
                                >
                                    {treeItems && treeItems.length > 0 && tree}
                                    {!treeItems ||
                                        (treeItems.length === 0 && (
                                            <EuiEmptyPrompt
                                                icon={
                                                    <EuiIcon
                                                        type="questionInCircle"
                                                        size="xxl"
                                                        color="subdued"
                                                    />
                                                }
                                                title={
                                                    <h4>
                                                        {t(
                                                            `network:no_${treeMode}_title`
                                                        )}
                                                    </h4>
                                                }
                                                body={
                                                    <p>
                                                        {t(
                                                            `network:no_${treeMode}_text`
                                                        )}
                                                    </p>
                                                }
                                                actions={
                                                    <EuiButton
                                                        color="primary"
                                                        fill
                                                        onClick={
                                                            handleOnAddClick
                                                        }
                                                    >
                                                        {t(
                                                            `network:no_${treeMode}_add`
                                                        )}
                                                    </EuiButton>
                                                }
                                            />
                                        ))}
                                </div>
                            )
                        }}
                    </EuiResizeObserver>
                </div>
            </div>
        </>
    )
}

export default NetworkTree
